import React, {useCallback, useEffect} from 'react';
import {FormOperacionCapitalMarketsDetalle, OperacionCapitalMarketsDetalle} from "../types/types";
import {DialogProps} from "@mui/material/Dialog";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Typography} from "@mui/material";
import {FormOperacionCapitalMarketsDetalleFields, OperacionCapitalMarketsDetalleFields} from "../types/fields";
import {UseFormReturn, useWatch} from "react-hook-form";
import {AsyncSelect, ControlledDatePicker, ControlledTextField} from "../../../../../components/forms";
import {HttpCacheOperacion} from "../../../api/httpCacheOperacion";
import {EntityWithIdAndDescriptionFields} from "../../../../../types";
import CapitalMarketsDetalleCampoTransaccion from "./CapitalMarketsDetalleCampoTransaccion";
import {TextField} from "src/lib/itaptec/components_forms/src";
import {ControlledAutocomplete} from "../../../../../components/forms/ControlledAutocomplete";
import Divider from "@mui/material/Divider";

interface CapitalMarketsDetalleDialogProps extends DialogProps {
  codPais: number,
  formMethods: UseFormReturn<FormOperacionCapitalMarketsDetalle>,
  onSubmitForm: (data: FormOperacionCapitalMarketsDetalle) => void,
  onClose: () => void,
  detalle?: OperacionCapitalMarketsDetalle,
}

function CapitalMarketsDetalleDialog({
                                       codPais,
                                       formMethods,
                                       onSubmitForm,
                                       onClose,
                                       detalle,
                                       ...props
                                     }: CapitalMarketsDetalleDialogProps) {
  const {control, handleSubmit, watch, setValue, formState: {errors}} = formMethods

  const getRatingTipos = useCallback(() => HttpCacheOperacion.getCapitalMarketsRatingTipos(codPais), [codPais])
  const getDestinoFondoTipos = useCallback(() => HttpCacheOperacion.getCapitalMarketsDestinoFondoTipos(codPais), [codPais])
  const getAmortizacionTipos = useCallback(() => HttpCacheOperacion.getCapitalMarketsAmortizacionTipos(codPais), [codPais])
  const getTransaccionTipos = useCallback(() => HttpCacheOperacion.getCapitalMarketsTransaccionTipos(codPais), [codPais])
  const watchListaDestinoDeFondos = useWatch({
    control: control,
    name: FormOperacionCapitalMarketsDetalleFields.ListaDestinoDeFondos
  })

  const watchListaAmortizacion = useWatch({
    control: control,
    name: FormOperacionCapitalMarketsDetalleFields.ListaAmortizacion
  })
  const watchTransaccion = useWatch({control: control, name: OperacionCapitalMarketsDetalleFields.Transaccion})

  useEffect(() => {
    setValue(OperacionCapitalMarketsDetalleFields.DestinoDeFondos, watchListaDestinoDeFondos?.join(', ') ?? '')
  }, [watchListaDestinoDeFondos])

  useEffect(() => {
    setValue(OperacionCapitalMarketsDetalleFields.Amortizacion, watchListaAmortizacion?.join(', ') ?? '')
  }, [watchListaAmortizacion])

  return (
    <Dialog open={props.open} maxWidth='md' fullWidth keepMounted>
      <DialogTitle>{`${detalle ? 'Editar' : 'Nuevo'} Detalle`}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{mt: 1}}>
          <Grid item xs={12} md={4}>
            <ControlledTextField
              control={control}
              name={OperacionCapitalMarketsDetalleFields.Emisor}
              label='Emisor'
              fullWidth
              size='small'
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <ControlledTextField
              control={control}
              name={OperacionCapitalMarketsDetalleFields.ValorNominal}
              label='Valor Nominal'
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CapitalMarketsDetalleCampoTransaccion formMethods={formMethods} getOpciones={getTransaccionTipos}/>
          </Grid>
          {
            watchTransaccion === 'Lending' &&
              <>
                  <Grid item container xs={12} spacing={3}>
                      <Grid item xs={12}>
                          <Divider style={{width: '100%'}}>
                              <Typography variant='caption'>Datos Transacción Lending</Typography>
                          </Divider>
                      </Grid>
                      <Grid item md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingDeudor}
                              label='Deudor'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingAcreedor}
                              label='Acreedor'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingMonto}
                              label='Monto'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingMoneda}
                              label='Moneda'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingDestino}
                              label='Destino de fondos'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingPlazoDisponibilidad}
                              label='Período de disponibilidad'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingPlazo}
                              label='Plazo'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingAmortizacion}
                              label='Amortización'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingTasaInteres}
                              label='Tasa de interés'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingFrecuenciaInteres}
                              label='Frecuencia de pago de interés'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingGarantias}
                              label='Garantías'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingComision}
                              label='Comisión'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingPrecancelacion}
                              label='Pre cancelación'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingCovenants}
                              label='Covenants'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12} md={4}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingLey}
                              label='Ley y jurisdicción'
                              fullWidth
                              size='small'
                          />
                      </Grid>
                      <Grid item xs={12}>
                          <ControlledDatePicker
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingFechaEjecucion}
                              label='Fecha de Ejecución'
                              fullWidth
                          />
                      </Grid>
                      <Grid item xs={12}>
                          <ControlledTextField
                              control={control}
                              name={OperacionCapitalMarketsDetalleFields.LendingOtros}
                              label='Otros'
                              fullWidth
                              size='small'
                              multiline
                          />
                      </Grid>
                      <Grid item xs={12}>
                          <Divider style={{width: '100%'}}/>

                      </Grid>
                  </Grid>
              </>
          }
          <Grid item xs={12} md={4}>
            <ControlledTextField
              control={control}
              name={OperacionCapitalMarketsDetalleFields.Moneda}
              label='Moneda'
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ControlledTextField
              control={control}
              name={OperacionCapitalMarketsDetalleFields.Plazo}
              label='Plazo'
              fullWidth
              size='small'
            />
          </Grid>

          {/*<Grid item xs={12} md={4}>*/}
          {/*  <AsyncSelect loadOptions={getAmortizacionTipos}*/}
          {/*               control={control}*/}
          {/*               name={OperacionCapitalMarketsDetalleFields.Amortizacion}*/}
          {/*               label='Amortización'*/}
          {/*               valueField={EntityWithIdAndDescriptionFields.Description}*/}
          {/*               selectNotNulleable*/}
          {/*               fullWidth*/}
          {/*  />*/}
          {/*</Grid>*/}

          <Grid item xs={12} md={4}>
            <ControlledAutocomplete
              label='Amortización'
              name={FormOperacionCapitalMarketsDetalleFields.ListaAmortizacion}
              control={control}
              multiple
              valueField={EntityWithIdAndDescriptionFields.Description}
              loadOptions={getAmortizacionTipos}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <ControlledTextField
              control={control}
              name={OperacionCapitalMarketsDetalleFields.Colocadores}
              label='Colocadores'
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <AsyncSelect loadOptions={getRatingTipos}
                         control={control}
                         name={OperacionCapitalMarketsDetalleFields.Rating}
                         label='Rating'
                         valueField={EntityWithIdAndDescriptionFields.Description}
                         selectNotNulleable
                         fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ControlledAutocomplete
              label='Destino de Fondos'
              name={FormOperacionCapitalMarketsDetalleFields.ListaDestinoDeFondos}
              control={control}
              multiple
              valueField={EntityWithIdAndDescriptionFields.Description}
              loadOptions={getDestinoFondoTipos}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              control={control}
              name={OperacionCapitalMarketsDetalleFields.Bridge}
              label='Bridge (moneda y monto)'
              fullWidth
              size='small'
              mask={{
                mask: '{a} {0}',
                blocks: {
                  a: {
                    mask: /^[A-Za-z]+$/,
                  },
                  0: {
                    mask: /^[0-9.]+$/,
                  }
                }
              }}
              helperText={
              <Stack direction={'column'}>
                <span>{errors?.[OperacionCapitalMarketsDetalleFields.Bridge]?.message}</span>

                <span>Ej. USD 1234</span>
              </Stack>}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ControlledTextField
              control={control}
              name={OperacionCapitalMarketsDetalleFields.Underwriting}
              label='Underwriting'
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledTextField
              control={control}
              name={OperacionCapitalMarketsDetalleFields.FEE}
              label='FEE'
              fullWidth
              size='small'
              multiline
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledTextField
              control={control}
              name={OperacionCapitalMarketsDetalleFields.Nota}
              label='Nota'
              fullWidth
              size='small'
              multiline
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant='text' color='primary' onClick={onClose}>
          Cancelar
        </Button>
        <Button variant='contained' color='primary' onClick={handleSubmit(onSubmitForm)}>
          {detalle ? 'Editar' : 'Agregar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CapitalMarketsDetalleDialog;